import { Component, createRef } from "react";

import {
  Stack,
  Typography,
  Button,
  Unstable_Grid2 as Grid,
  Avatar,
  LinearProgress,
  LinearProgressProps,
  Box,
} from "@mui/material";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import { v4 as uuidv4 } from "uuid";

import api from "../../services/api";

import { humanFileSize } from "../../utils";

import useAuthStore from "../../stores/auth";
import TagManager from "react-gtm-module";

interface Props {
  baseUrl: string;
  multiple: boolean;
  allowedFiles: string;
  callback: (response: any) => void;
  enableDelete?: boolean;
  onDelete?: (file: any) => void;
  fileNameTransform?: (fileName: string) => string;
  initialFiles?: Array<any>;
  onUploadingChange?: (isUploading: boolean) => void;
}

interface State {
  isUploading: boolean;
  dragActive: boolean;
  uploadedFiles: Array<any>;
  inputRef: any;
}

function LinearProgressWithLabel(
  props: LinearProgressProps & { value: number },
) {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ width: "100%", mr: 1 }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" color="text.secondary">{`${Math.round(
          props.value,
        )}%`}</Typography>
      </Box>
    </Box>
  );
}

class DragAndDropFiles extends Component<Props, State> {
  static defaultProps: Partial<Props> = {
    multiple: true,
    enableDelete: true,
  };

  private inputId: string;

  constructor(props: Props) {
    super(props);
    this.inputId = `file-upload-${uuidv4()}`;
    const initialFiles =
      props.initialFiles?.map((file: any) => ({
        ...file,
        id: file.id || uuidv4(),
        uploaded: true,
        progress: 100,
      })) || [];

    this.state = {
      isUploading: false,
      dragActive: false,
      uploadedFiles: initialFiles,
      inputRef: createRef(),
    };
  }

  hasPendingUploads = () =>
    this.state.uploadedFiles.some((file) => !file.uploaded && !file.error);

  notifyUploadingChange = () => {
    if (this.props.onUploadingChange) {
      this.props.onUploadingChange(this.hasPendingUploads());
    }
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.uploadedFiles !== this.state.uploadedFiles) {
      this.notifyUploadingChange();
    }

    if (prevProps.initialFiles === this.props.initialFiles) {
      return;
    }

    if (this.hasPendingUploads()) {
      return;
    }

    const initialFiles =
      this.props.initialFiles?.map((file: any) => ({
        ...file,
        id: file.id || uuidv4(),
        uploaded: true,
        progress: 100,
      })) || [];

    this.setState({ uploadedFiles: initialFiles });
  }

  handleDrag = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      this.setState({ dragActive: true });
    } else if (e.type === "dragleave") {
      this.setState({ dragActive: false });
    }
  };

  handleDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    this.setState({ dragActive: false });

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      this.handleUpload(e.dataTransfer.files);
    }
  };

  handleChange = (e: any) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      this.handleUpload(e.target.files);
    }
  };

  onButtonClick = () => {
    if (this.state.inputRef.current != null) {
      this.state.inputRef.current.click();
    }
  };

  handleUpload = (files: any) => {
    const organization = useAuthStore.getState().company;
    const user = useAuthStore.getState().user;
    const newFiles = Array.from(files).map((file: any) => {
      let normalizedName = file.name
        .normalize("NFD")
        .replace(/[^a-zA-Z0-9.\s]/g, "");
      if (this.props.fileNameTransform) {
        normalizedName = this.props.fileNameTransform(normalizedName);
      }
      return {
        file,
        id: uuidv4(),
        name: normalizedName,
        originalName: file.name,
        size: humanFileSize(file.size),
        progress: 0,
        uploaded: false,
        error: false,
        url: null,
      };
    });

    this.setState((prev) => ({
      uploadedFiles: prev.uploadedFiles.concat(newFiles),
    }));

    TagManager.dataLayer({
      dataLayer: {
        event: `sent_files`,
        organization,
        user,
      },
    });

    newFiles.forEach(this.processUpload);
  };

  updateFile = (id: string, data: any) => {
    this.setState((prev) => ({
      uploadedFiles: prev.uploadedFiles.map((uploadedFile: any) =>
        id === uploadedFile.id ? { ...uploadedFile, ...data } : uploadedFile,
      ),
    }));
  };

  handleDelete = (file: any) => {
    if (this.props.onDelete) {
      this.props.onDelete(file);
    }
    this.setState((prev) => ({
      uploadedFiles: prev.uploadedFiles.filter((f: any) => f.id !== file.id),
    }));
  };

  componentWillUnmount() {
    if (this.props.onUploadingChange) {
      this.props.onUploadingChange(false);
    }
  }

  processUpload = (uploadedFile: any) => {
    const data = new FormData();

    data.append("file", uploadedFile.file, uploadedFile.name);

    if (!this.props.multiple) {
      this.setState({ isUploading: true });
    }

    api()
      .post(this.props.baseUrl, data, {
        onUploadProgress: (e) => {
          const progress = Math.round((e.loaded * 100) / e.total);

          this.updateFile(uploadedFile.id, { progress });
        },
      })
      .then((response) => {
        if (this.props.callback) {
          this.props.callback({
            ...response,
            fileSize: uploadedFile.file.size,
            fileName: uploadedFile.originalName || uploadedFile.name,
          });
        }
        this.updateFile(uploadedFile.id, {
          uploaded: true,
        });

        if (!this.props.multiple) {
          this.setState({ isUploading: false });
        }
      })
      .catch(() => {
        this.updateFile(uploadedFile.id, {
          error: true,
        });
      });
  };

  render() {
    return (
      <>
        <div
          id="drag-drop-upload"
          onDragEnter={
            this.props.multiple
              ? this.handleDrag
              : () => {
                  return false;
                }
          }
          onDragLeave={
            this.props.multiple
              ? this.handleDrag
              : () => {
                  return false;
                }
          }
          onDragOver={
            this.props.multiple
              ? this.handleDrag
              : () => {
                  return false;
                }
          }
          onDrop={
            this.props.multiple
              ? this.handleDrop
              : () => {
                  return false;
                }
          }
        >
          <input
            id={this.inputId}
            ref={this.state.inputRef}
            hidden
            multiple={this.props.multiple}
            accept={this.props.allowedFiles}
            type="file"
            onChange={this.handleChange}
          />
          <Stack
            spacing={1}
            direction="column"
            justifyContent="center"
            alignItems="center"
            sx={{
              p: 3,
              border: "1px dashed #000",
              borderColor: "primary.main",
              borderRadius: 1,
              backgroundColor: this.state.dragActive
                ? "#009aff2b"
                : "transparent",
            }}
          >
            <CloudUploadIcon sx={{ fontSize: "5em" }} color="primary" />
            {this.props.multiple && (
              <Typography textAlign="center">
                {this.state.dragActive
                  ? "Agora solte o(s) arquivo(s)"
                  : "Arraste e solte arquivos aqui ou"}
              </Typography>
            )}
            <Button
              type="button"
              variant="contained"
              size="small"
              onClick={this.onButtonClick}
              disabled={
                this.props.multiple
                  ? this.state.dragActive
                  : this.state.isUploading
              }
            >
              Selecione um arquivo
            </Button>
          </Stack>
        </div>
        <Grid container sx={{ mt: 2 }} spacing={2}>
          {this.state.uploadedFiles &&
            this.state.uploadedFiles.map((file: any) => (
              <Grid key={file.id} xs={12}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignContent="center"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderColor: file.uploaded
                      ? "secondary.main"
                      : "primary.main",
                  }}
                >
                  <Avatar
                    sx={{
                      fontSize: 14,
                      fontWeight: "bold",
                      bgcolor: "primary.light",
                    }}
                  >
                    {(file.originalName || file.name)
                      .split(".")
                      .pop()
                      .toLocaleUpperCase()}
                  </Avatar>
                  <Box sx={{ width: "100%", flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        width: "100%",
                        fontWeight: "bold",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      component="div"
                      variant="body2"
                      title={file.originalName || file.name}
                    >
                      {file.originalName || file.name}
                    </Typography>
                    <Typography component="div" variant="caption">
                      {file.size}
                    </Typography>
                    {!file.uploaded ? (
                      <Box sx={{ width: "100%" }}>
                        <LinearProgressWithLabel
                          value={file.uploaded ? 100 : file.progress}
                        />
                      </Box>
                    ) : (
                      <Typography
                        color="success"
                        component="div"
                        variant="caption"
                      >
                        Envio concluído.
                      </Typography>
                    )}
                  </Box>
                  {this.props.enableDelete && file.uploaded && (
                    <Button
                      size="small"
                      onClick={() => this.handleDelete(file)}
                      color="error"
                      variant="outlined"
                      sx={{ ml: 1, minWidth: "auto" }}
                    >
                      Excluir
                    </Button>
                  )}
                </Stack>
              </Grid>
            ))}
        </Grid>
      </>
    );
  }
}

export default DragAndDropFiles;
